import { ReactNode, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useSubscription } from '@/hooks/useSubscription';
import { PaywallModal } from '../subscription/PaywallModal';
import { Button } from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import { Lock, Crown, Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
  requireSubscription?: boolean;
  feature?: string;
}

export function ProtectedRoute({
  children,
  requireAuth = false,
  requireSubscription = false,
  feature = 'this feature'
}: ProtectedRouteProps) {
  const { isAuthenticated, isAuthInitialized, setShowAuthModal, setAuthModalMode } = useAuthStore();
  const { hasAccess, isLoading: subscriptionLoading } = useSubscription();
  const [showPaywall, setShowPaywall] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to home if not authenticated and not already on home page
  // Wait for auth to be initialized before making redirect decisions
  useEffect(() => {
    if (isAuthInitialized && requireAuth && !isAuthenticated && location.pathname !== '/') {
      navigate('/');
    }
  }, [isAuthInitialized, requireAuth, isAuthenticated, navigate, location.pathname]);

  // Show loading state while auth is being initialized (safety net - sync init should make this rare)
  if (!isAuthInitialized && requireAuth) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Check authentication (only after initialization)
  if (requireAuth && !isAuthenticated) {
    // If not on home page, return null while redirecting
    if (location.pathname !== '/') {
      return null;
    }

    // Show auth prompt on home page
    return (
      <Card className="max-w-md mx-auto mt-8">
        <CardContent className="p-6 text-center space-y-4">
          <div className="flex justify-center">
            <div className="rounded-full bg-blue-100 p-3">
              <Lock className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold">Welcome to GodMode</h3>
            <p className="text-muted-foreground mt-2">
              Sign in to access your aspirations, goals, and lists.
            </p>
          </div>

          <div className="space-y-2">
            <Button
              className="w-full"
              onClick={() => {
                setAuthModalMode('signin');
                setShowAuthModal(true);
              }}
            >
              Sign In
            </Button>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setAuthModalMode('signup');
                setShowAuthModal(true);
              }}
            >
              Create Account
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Your data will remain on this device. Signing in enables cloud sync and cross-device access.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Check subscription (for premium features)
  if (requireSubscription) {
    // Show loading state while checking subscription
    if (subscriptionLoading) {
      return (
        <Card className="max-w-md mx-auto mt-8">
          <CardContent className="p-6 text-center space-y-4">
            <div className="flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">Checking subscription...</p>
          </CardContent>
        </Card>
      );
    }

    if (!hasAccess) {
      return (
        <>
          <Card className="max-w-md mx-auto mt-8">
            <CardContent className="p-6 text-center space-y-4">
              <div className="flex justify-center">
                <div className="rounded-full bg-purple-100 p-3">
                  <Crown className="h-8 w-8 text-purple-600" />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold">Premium Feature</h3>
                <p className="text-muted-foreground mt-2">
                  {feature} requires a Pro subscription.
                </p>
              </div>

              <div className="space-y-2">
                <Button className="w-full" onClick={() => setShowPaywall(true)}>
                  <Crown className="h-4 w-4 mr-2" />
                  Upgrade to Pro
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">
                Start your 14-day free trial. Cancel anytime.
              </p>
            </CardContent>
          </Card>

          <PaywallModal
            isOpen={showPaywall}
            onClose={() => setShowPaywall(false)}
            feature={feature}
          />
        </>
      );
    }
  }

  return <>{children}</>;
}

// Higher-order component for wrapping pages
export function withAuth<T extends object>(
  Component: React.ComponentType<T>,
  options: { requireAuth?: boolean; requireSubscription?: boolean; feature?: string } = {}
) {
  return function AuthenticatedComponent(props: T) {
    return (
      <ProtectedRoute {...options}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}